// src/features/analytics/DataSourceConfigEditor.tsx
// Editor for a single DataSourceConfig — product/entity picker (from
// ListAvailableDataSources), metrics, filters, group-by, time range and
// ordering. Shared by the widget dialog, saved-query dialog and the report
// generation dialog (a report holds a repeated list of these).

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Button } from '@react/components';
import { Select } from '@/components/Select';
import type { AvailableDataSource, DataSourceConfig, MetricConfig, DataFilter } from '@core/types/analytics';
import { AGGREGATIONS, FILTER_OPERATORS, TIME_GRANULARITIES, TIME_RANGE_TYPES, parseClearableInt } from './helpers';

interface DataSourceConfigEditorProps {
  value: DataSourceConfig;
  onChange: (next: DataSourceConfig) => void;
  dataSources: AvailableDataSource[];
}

export function DataSourceConfigEditor({ value, onChange, dataSources }: DataSourceConfigEditorProps) {
  const { t } = useI18n();
  const [groupByDraft, setGroupByDraft] = useState('');

  const selectedSource = dataSources.find((d) => d.product_slug === value.product_slug);
  const entityTypeOptions = selectedSource?.entity_types ?? [];
  const fieldOptions = selectedSource?.queryable_fields ?? [];

  const patch = (fields: Partial<DataSourceConfig>) => onChange({ ...value, ...fields });

  const metrics = value.metrics ?? [];
  const setMetrics = (next: MetricConfig[]) => patch({ metrics: next });
  const addMetric = () =>
    setMetrics([...metrics, { field: fieldOptions[0] ?? '*', aggregation: 'AGGREGATION_COUNT', alias: '', format: '', percentile_value: 0 }]);
  const updateMetric = (i: number, patchM: Partial<MetricConfig>) =>
    setMetrics(metrics.map((m, idx) => (idx === i ? { ...m, ...patchM } : m)));
  const removeMetric = (i: number) => setMetrics(metrics.filter((_, idx) => idx !== i));

  const filters = value.filters ?? [];
  const setFilters = (next: DataFilter[]) => patch({ filters: next });
  const addFilter = () => setFilters([...filters, { field: fieldOptions[0] ?? '', operator: 'eq', value: '', values: [] }]);
  const updateFilter = (i: number, patchF: Partial<DataFilter>) =>
    setFilters(filters.map((f, idx) => (idx === i ? { ...f, ...patchF } : f)));
  const removeFilter = (i: number) => setFilters(filters.filter((_, idx) => idx !== i));

  const groupBy = value.group_by ?? [];
  const addGroupBy = () => {
    const v = groupByDraft.trim();
    if (v && !groupBy.includes(v)) patch({ group_by: [...groupBy, v] });
    setGroupByDraft('');
  };
  const removeGroupBy = (field: string) => patch({ group_by: groupBy.filter((g) => g !== field) });

  const timeRange = value.time_range ?? { type: 'TIME_RANGE_TYPE_RELATIVE', relative_value: 7, relative_unit: 'days', absolute_start: '', absolute_end: '' };
  const setTimeRange = (patchT: Partial<typeof timeRange>) => patch({ time_range: { ...timeRange, ...patchT } });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Select
          label={t('analytics.query.product')}
          value={value.product_slug}
          onChange={(e) => patch({ product_slug: e.target.value, entity_type: '' })}
        >
          <option value="">{t('common.select')}</option>
          {dataSources.map((d) => (
            <option key={d.product_slug} value={d.product_slug}>
              {d.product_name}
            </option>
          ))}
        </Select>
        <Select
          label={t('analytics.query.entityType')}
          value={value.entity_type}
          onChange={(e) => patch({ entity_type: e.target.value })}
          disabled={entityTypeOptions.length === 0}
        >
          <option value="">{t('common.select')}</option>
          {entityTypeOptions.map((et) => (
            <option key={et} value={et}>
              {et}
            </option>
          ))}
        </Select>
      </div>

      {/* Metrics */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">{t('analytics.query.metrics')}</span>
          <Button type="button" size="sm" variant="secondary" onClick={addMetric}>
            {t('analytics.query.addMetric')}
          </Button>
        </div>
        {metrics.length === 0 && <p className="text-xs text-muted-foreground">{t('analytics.query.noMetrics')}</p>}
        {metrics.map((m, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2">
            <Input
              label={i === 0 ? t('analytics.query.field') : undefined}
              value={m.field}
              onChange={(e) => updateMetric(i, { field: e.target.value })}
              placeholder="*"
            />
            <Select label={i === 0 ? t('analytics.query.aggregation') : undefined} value={m.aggregation ?? 'AGGREGATION_COUNT'} onChange={(e) => updateMetric(i, { aggregation: e.target.value as MetricConfig['aggregation'] })}>
              {AGGREGATIONS.map((a) => (
                <option key={a} value={a}>
                  {t(`analytics.aggregation.${a}`)}
                </option>
              ))}
            </Select>
            <Input label={i === 0 ? t('analytics.query.alias') : undefined} value={m.alias} onChange={(e) => updateMetric(i, { alias: e.target.value })} />
            <Button type="button" size="sm" variant="ghost" onClick={() => removeMetric(i)} aria-label={t('common.remove')}>
              {t('common.remove')}
            </Button>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">{t('analytics.query.filters')}</span>
          <Button type="button" size="sm" variant="secondary" onClick={addFilter}>
            {t('analytics.query.addFilter')}
          </Button>
        </div>
        {filters.map((f, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2">
            <Input value={f.field} onChange={(e) => updateFilter(i, { field: e.target.value })} placeholder={t('analytics.query.field')} />
            <Select aria-label={t('analytics.query.operator')} value={f.operator} onChange={(e) => updateFilter(i, { operator: e.target.value })}>
              {FILTER_OPERATORS.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </Select>
            <Input value={f.value} onChange={(e) => updateFilter(i, { value: e.target.value })} placeholder={t('analytics.query.value')} />
            <Button type="button" size="sm" variant="ghost" onClick={() => removeFilter(i)} aria-label={t('common.remove')}>
              {t('common.remove')}
            </Button>
          </div>
        ))}
      </div>

      {/* Group by */}
      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">{t('analytics.query.groupBy')}</span>
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background p-2">
          {groupBy.map((g) => (
            <span key={g} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground">
              {g}
              <button type="button" onClick={() => removeGroupBy(g)} aria-label={t('common.remove')} className="text-muted-foreground hover:text-destructive">
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={groupByDraft}
            onChange={(e) => setGroupByDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addGroupBy();
              }
            }}
            onBlur={addGroupBy}
            placeholder={groupBy.length === 0 ? t('analytics.query.groupByPlaceholder') : undefined}
            className="min-w-[8rem] flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* Time range */}
      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">{t('analytics.query.timeRange')}</span>
        <div className="grid grid-cols-2 gap-3">
          <Select value={timeRange.type ?? 'TIME_RANGE_TYPE_RELATIVE'} onChange={(e) => setTimeRange({ type: e.target.value as typeof timeRange.type })}>
            {TIME_RANGE_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {t(`analytics.timeRangeType.${tt}`)}
              </option>
            ))}
          </Select>
          <Select value={value.time_granularity ?? 'TIME_GRANULARITY_DAY'} onChange={(e) => patch({ time_granularity: e.target.value as DataSourceConfig['time_granularity'] })}>
            {TIME_GRANULARITIES.map((g) => (
              <option key={g} value={g}>
                {t(`analytics.granularity.${g}`)}
              </option>
            ))}
          </Select>
        </div>
        {timeRange.type === 'TIME_RANGE_TYPE_ABSOLUTE' ? (
          <div className="grid grid-cols-2 gap-3">
            <Input type="datetime-local" label={t('analytics.query.start')} value={timeRange.absolute_start} onChange={(e) => setTimeRange({ absolute_start: e.target.value })} />
            <Input type="datetime-local" label={t('analytics.query.end')} value={timeRange.absolute_end} onChange={(e) => setTimeRange({ absolute_end: e.target.value })} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t('analytics.query.relativeValue')}
              value={timeRange.relative_value ? String(timeRange.relative_value) : ''}
              onChange={(e) => setTimeRange({ relative_value: parseClearableInt(e.target.value) ?? 0 })}
              inputMode="numeric"
            />
            <Select label={t('analytics.query.relativeUnit')} value={timeRange.relative_unit || 'days'} onChange={(e) => setTimeRange({ relative_unit: e.target.value })}>
              <option value="minutes">{t('analytics.unit.minutes')}</option>
              <option value="hours">{t('analytics.unit.hours')}</option>
              <option value="days">{t('analytics.unit.days')}</option>
              <option value="weeks">{t('analytics.unit.weeks')}</option>
              <option value="months">{t('analytics.unit.months')}</option>
            </Select>
          </div>
        )}
      </div>

      {/* Order + limit */}
      <div className="grid grid-cols-3 gap-3">
        <Input label={t('analytics.query.orderBy')} value={value.order_by} onChange={(e) => patch({ order_by: e.target.value })} />
        <Select label={t('analytics.query.orderDirection')} value={value.order_direction || 'desc'} onChange={(e) => patch({ order_direction: e.target.value })}>
          <option value="asc">{t('analytics.query.asc')}</option>
          <option value="desc">{t('analytics.query.desc')}</option>
        </Select>
        <Input
          label={t('analytics.query.limit')}
          value={value.limit ? String(value.limit) : ''}
          onChange={(e) => patch({ limit: parseClearableInt(e.target.value) ?? 0 })}
          inputMode="numeric"
        />
      </div>
    </div>
  );
}
